import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Award, Trophy, TrendingUp, Gift, Lock, History, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import api from '../services/api';
import { toast } from 'sonner';

const Rewards = () => {
  const { user } = useAuth();
  const { pointsSystemEnabled, loading: tierLoading } = useTier();
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState({
    total_points: 0,
    tier: 'Silver',
    level: 'Newcomer',
    next_level_points: 500,
    points_to_next_level: 500,
    achievements: [],
    earned_count: 0,
    total_achievements: 10,
    recent_transactions: []
  });

  useEffect(() => {
    if (user?.member_id) {
      fetchRewards();
    }
  }, [user]);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/rewards/${user.member_id}`);
      setRewards(response.data);
    } catch (error) {
      console.error('Failed to load rewards:', error);
      // Use fallback data on error
    } finally {
      setLoading(false);
    }
  };

  const earnedAchievements = rewards.achievements?.filter((a) => a.earned) || [];
  const upcomingAchievements = rewards.achievements?.filter((a) => !a.earned) || [];
  const progressPercentage = rewards.next_level_points > 0 
    ? (rewards.total_points / rewards.next_level_points) * 100 
    : 100;

  if (!tierLoading && !pointsSystemEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rewards & Achievements</h1>
          <p className="text-gray-600 mt-1">Loading your rewards...</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-24 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Rewards & Achievements</h1>
        <p className="text-gray-600 mt-1">Track your progress and unlock exclusive benefits</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-amber-900">Total Points</span>
              <Award className="text-amber-600" size={24} />
            </div>
            <p className="text-4xl font-bold text-amber-700">{rewards.total_points.toLocaleString()}</p>
            <p className="text-sm text-amber-600 mt-2">{rewards.level}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">Achievements</span>
              <Trophy className="text-blue-600" size={24} />
            </div>
            <p className="text-4xl font-bold text-blue-700">
              {rewards.earned_count}/{rewards.total_achievements}
            </p>
            <p className="text-sm text-blue-600 mt-2">Unlocked</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-900">Next Level</span>
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <p className="text-4xl font-bold text-green-700">
              {rewards.points_to_next_level.toLocaleString()}
            </p>
            <p className="text-sm text-green-600 mt-2">Points to go</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Level Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">{rewards.level}</span>
              <span className="text-gray-600">
                {rewards.total_points.toLocaleString()} / {rewards.next_level_points.toLocaleString()} points
              </span>
            </div>
            <Progress value={Math.min(progressPercentage, 100)} className="h-3" />
            <p className="text-sm text-gray-600">
              {Math.round(progressPercentage)}% complete - Keep earning points to reach the next level!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      {rewards.recent_transactions && rewards.recent_transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <History size={20} className="mr-2" />
              Recent Points Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rewards.recent_transactions.slice(0, 5).map((txn, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">{txn.description}</p>
                    <p className="text-xs text-gray-500">{txn.type}</p>
                  </div>
                  <span className={`font-bold ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {txn.amount > 0 ? '+' : ''}{txn.amount}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {earnedAchievements.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unlocked Achievements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {earnedAchievements.map((achievement, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-4xl mb-3">{achievement.icon}</div>
                      <h3 className="font-semibold text-gray-900 mb-1">{achievement.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge className="bg-green-600 text-white">
                          <Gift size={12} className="mr-1" />
                          +{achievement.points} pts
                        </Badge>
                        {achievement.earned_date && (
                          <span className="text-xs text-gray-500">
                            {new Date(achievement.earned_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {upcomingAchievements.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Upcoming Achievements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingAchievements.map((achievement, idx) => (
              <Card key={idx} className="bg-gray-50 border-gray-200 relative overflow-hidden">
                <div className="absolute inset-0 bg-gray-100/50 backdrop-blur-sm flex items-center justify-center">
                  <Lock size={48} className="text-gray-400" />
                </div>
                <CardContent className="p-6 opacity-60">
                  <div className="text-4xl mb-3 grayscale">{achievement.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-1">{achievement.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                  <Badge variant="outline" className="bg-white">
                    <Gift size={12} className="mr-1" />
                    +{achievement.points} pts
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="p-8">
          <div className="text-center max-w-2xl mx-auto">
            <Trophy size={48} className="mx-auto mb-4 text-purple-600" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">How to Earn Points</h3>
            <ul className="text-left space-y-2 text-gray-700">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-600 rounded-full mr-3"></span>
                Complete successful deals and transactions
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-600 rounded-full mr-3"></span>
                Connect with new members in the network
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-600 rounded-full mr-3"></span>
                Refer new partners to the alliance
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-600 rounded-full mr-3"></span>
                Attend events and participate in the community
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-600 rounded-full mr-3"></span>
                Maintain high response rates and quality service
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Rewards;
